import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import type { PromoCode } from '@Types/promoCode';
import { promoCodeTypeLabels } from '@Types/promoCode';
import { buildFilterParams } from '@Utils/buildFilterParams';
import { usePromoCodes } from '@Hooks/usePromoCodes';
import { useDeletePromoCode } from '@Hooks/useDeletePromoCode';
import { Pencil, Percent, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PromoCodeSheet from './PromoCodeSheet';
import ConfirmationDialog from '@Components/ConfirmationDialog';
import ShowFor from '@Components/ShowFor';

const PROMO_CODE_FIELD_MAPPING: Record<string, string> = {
  code: 'filter[code]',
};

const formatDiscount = (row: PromoCode) => {
  if (row.discountType === 'PERCENTAGE') return `${row.discountValue}%`;
  if (row.discountType === 'FLATAMOUNT') return `$${row.discountValue}`;
  return `$${row.discountValue} (override)`;
};

const promoCodeColumnConfigs: ColumnConfig<PromoCode>[] = [
  {
    id: 'code',
    headerLabel: 'Code',
    accessor: (row) => row.code,
    type: 'text',
    size: 180,
    cell: (info) => (
      <Link
        to={`/promo-codes/${info.row.original.id}`}
        className="text-primary hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {info.getValue() as string}
      </Link>
    ),
  },
  {
    id: 'codeType',
    headerLabel: 'Code Type',
    accessor: (row) => promoCodeTypeLabels[row.codeType],
    type: 'text',
    size: 120,
  },
  {
    id: 'discountValue',
    headerLabel: 'Discount',
    accessor: (row) => formatDiscount(row),
    type: 'text',
    size: 140,
  },
  {
    id: 'isActive',
    headerLabel: 'Status',
    accessor: (row) => (row.isActive ? 'Active' : 'Inactive'),
    type: 'text',
    size: 100,
    cell: (info) => {
      const isActive = info.row.original.isActive;
      return (
        <span
          className={isActive ? 'font-medium text-green-600' : 'text-zinc-400'}
        >
          {isActive ? 'Active' : 'Inactive'}
        </span>
      );
    },
  },
  {
    id: 'description',
    headerLabel: 'Description',
    accessor: (row) => row.description ?? '-',
    type: 'text',
    size: 200,
  },
  {
    id: 'validFrom',
    headerLabel: 'Valid From',
    accessor: (row) => new Date(row.validFrom).toLocaleDateString(),
    type: 'text',
    size: 120,
  },
  {
    id: 'validUntil',
    headerLabel: 'Valid Until',
    accessor: (row) =>
      row.validUntil
        ? new Date(row.validUntil).toLocaleDateString()
        : 'No expiry',
    type: 'text',
    size: 120,
  },
  {
    id: 'maxRedemptions',
    headerLabel: 'Max Redemptions',
    accessor: (row) => row.maxRedemptions ?? 'Unlimited',
    type: 'text',
    size: 140,
  },
];

export default function PromoCodeList() {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedPromoCode, setSelectedPromoCode] = useState<PromoCode | null>(
    null,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promoCodeToDelete, setPromoCodeToDelete] = useState<PromoCode | null>(
    null,
  );

  const deleteMutation = useDeletePromoCode();

  const controller = useResourceTableController<PromoCode>({
    columnsConfig: promoCodeColumnConfigs,
    defaultSort: {
      id: 'code',
      desc: false,
    },
    buildFilterParams: (filters) =>
      buildFilterParams(filters, {
        fieldMapping: PROMO_CODE_FIELD_MAPPING,
      }),
    quickFilterFields: [{ field: 'code', label: 'Code' }],
    initialPageSize: 10,
    rowActions: (row) => (
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" asChild>
          <Link
            to={`/promo-codes/${row.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Pencil className="h-4 w-4" />
          </Link>
        </Button>
        <ShowFor permissions={['PROMO_CODES_WRITE']}>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(row);
            }}
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </ShowFor>
      </div>
    ),
  });

  const {
    data: promoCodes,
    status,
    isError,
    error,
  } = usePromoCodes({ searchParams: controller.searchParams });

  useEffect(() => {
    if (isError && error) {
      toastError(
        error instanceof Error ? error.message : 'Failed to load Promo Codes',
      );
    }
  }, [isError, error]);

  const rows: PromoCode[] = promoCodes?.data ?? [];
  const meta = promoCodes?.meta;

  const handleCreate = () => {
    setSelectedPromoCode(null);
    setIsSheetOpen(true);
  };

  const handleDeleteClick = (promoCode: PromoCode) => {
    setPromoCodeToDelete(promoCode);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!promoCodeToDelete) return;

    try {
      await deleteMutation.mutateAsync({ id: promoCodeToDelete.id });
      toastSuccess(`"${promoCodeToDelete.code}" has been deleted.`);
      setDeleteDialogOpen(false);
      setPromoCodeToDelete(null);
    } catch (err) {
      toastError(
        err instanceof Error ? err.message : 'Failed to delete Promo Code',
      );
    }
  };

  const handleSheetSuccess = () => {
    toastSuccess(
      selectedPromoCode
        ? `"${selectedPromoCode.code}" has been updated.`
        : 'Promo Code has been created.',
    );
  };

  return (
    <>
      <ResourceTableView<PromoCode>
        label="Promo Codes"
        controller={controller}
        data={rows}
        meta={meta}
        status={status}
        summaryIcon={<Percent className="h-4 w-4 text-zinc-500" />}
        summaryLabel="Total Promo Codes: "
        renderExtraActions={() => (
          <ShowFor permissions={['PROMO_CODES_WRITE']}>
            <Button variant="primary" onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Create Promo Code
            </Button>
          </ShowFor>
        )}
      />

      <PromoCodeSheet
        promoCode={selectedPromoCode}
        isOpen={isSheetOpen}
        setIsOpen={setIsSheetOpen}
        onSuccess={handleSheetSuccess}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSubmit={handleDeleteConfirm}
        match={promoCodeToDelete?.code ?? ''}
        title="Delete Promo Code"
        description={`Are you sure you want to delete "${promoCodeToDelete?.code}"? This action cannot be undone.`}
        matchLabel="Type the promo code to confirm"
        submitLabel="Delete"
        isLoading={deleteMutation.isPending}
        variant="destructive"
      />
    </>
  );
}
